import { computed } from 'vue'
import { useStorage } from '@vueuse/core'
import { useTabStorage } from '@/composables/useTabSpecificStorage'
import { ILayoutItem, ILayoutParametersList, ILayoutUpsertItem } from '@/types/dashboard'
import { ActiveDashboardData } from '@/composables/globalData/useDashoardsData'
import { LayoutApi } from '@/api/layoutApi'
import {
    DEFAULT_FONT_SIZE,
    DEFAULT_LAYOUT_FONT_STYLE,
    DEFAULT_REFRESH_DELAY_VALUE,
    DEFAULT_REFRESH_INTERVAL_VALUE,
    defaultExtensionThresholdConfig,
    defaultLayout,
    extensionThresholdKeys,
    LAYOUT_PARAMETERS_DEFAULTS,
    LAYOUT_STORE_KEY
} from '@/enum/default-layout-config'
import i18n from '@/i18n'
import { layoutDefaultColors, layoutParametersColorKeys } from '@/enum/colors'
import { convertHex } from '@/helpers/convertHex'
import {
    allSoundList,
    DEFAULT_WARNING_SOUND,
    DEFAULT_ERROR_SOUND,
    NONE_SOUND_ID,
    getSoundObject,
    warningSoundList,
    errorSoundList
} from '@/composables/globalData/useEntitiesData'
import { isStatusIsCallStatus } from '@/enum/redesignStatusTypes'
import { getPreviewLayout, isPreviewLayout } from '@/composables/usePreviewLayout'
import { warningThresholdValue, limitThresholdValue } from '@/composables/useSoundsThresholdNotification'

/* Types */
type LayoutStoreType = ILayoutItem

/**
 * Status alert configuration structure for a single status
 * Note: Colors are global parameters (LoginStatusWarningColor, LoginStatusLimitColor)
 */
export interface StatusAlertConfig {
    warning: string | number
    limit: string | number
    warningSound: string | number
    limitSound: string | number
    disableStatusAlerts: boolean
}

/**
 * Map of status ID to its alert configuration
 */
export type StatusAlertsConfigMap = Record<number, StatusAlertConfig>

/* Storage */
const activeLayoutStore = useTabStorage<LayoutStoreType | undefined>(LAYOUT_STORE_KEY, defaultLayout())

/* Computed */
export const activeLayout = computed(() => {
    return activeLayoutStore.value || defaultLayout()
})
export const activeLayoutID = computed(() => {
    return activeLayout.value.LayoutID
})

export const activeLayoutParametersList = computed(() => {
    return activeLayout.value?.LayoutParametersList || []
})

export const showLayoutWidgetTitle = computed(() => {
    const DEFAULT_VALUE = true
    const params = activeLayoutParametersList.value
    const result = params.find((el) => el.LayoutParameterName === 'ShowWidgetTitles')
    if (result) {
        return Boolean(result['Value'] === '1')
    }
    return DEFAULT_VALUE
})
export const activeLayoutFontSize = computed(() => {
    const params = activeLayoutParametersList.value
    const result = params.find((el) => el.LayoutParameterName === 'WidgetTitlesFontSize' && el.Value)
    if (result) {
        return {
            fontSize: `${result.Value}px`,
        }
    }

    return DEFAULT_LAYOUT_FONT_STYLE
})
export const widgetGroupTitleStyles = computed(() => {
    // const { mainTextColor } = getActiveLayoutColors()
    const defaultSizeVar = DEFAULT_FONT_SIZE
    // const defaultColor = mainTextColor
    const defaultFontSize = `${DEFAULT_FONT_SIZE}px`

    if (!activeLayout.value.LayoutParametersList?.length) {
        return {
            '--size': defaultSizeVar,
            // color: defaultColor,
            fontSize: defaultFontSize,
        }
    }
    const fontSize = getActiveLayoutParameterByName('WidgetGroupTitlesFontSize', 'Value') || defaultSizeVar
    // const color = getActiveLayoutParameterByName('ColorWidgetGroupTitles', 'Value') || defaultColor

    return {
        '--size': fontSize,
        // color: String(color),
        fontSize: `${fontSize}px`,
    }
})
export const widgetTableContentFontSize = computed(() => {
    const DEFAULT_VALUE = {
        fontSize: '16px',
    }
    if (!activeLayout.value.LayoutParametersList?.length) {
        return DEFAULT_VALUE
    }

    const resultValue = getActiveLayoutParameterByName('WidgetTableContentFontSize', 'Value')

    return resultValue ? {
        fontSize: `${resultValue}px`
    } : DEFAULT_VALUE
})
export const widgetTableHeaderFontSize = computed(() => {
    const DEFAULT_VALUE = {
        fontSize: '16px',
    }
    if (!activeLayout.value.LayoutParametersList?.length) {
        return DEFAULT_VALUE
    }

    const resultValue = getActiveLayoutParameterByName('WidgetTableHeaderFontSize', 'Value')

    return resultValue ? {
        fontSize: `${resultValue}px`
    } : DEFAULT_VALUE
})
export const layoutSwitchReport = computed(() => {
    const DEFAULT_VALUE = false
    if (!activeLayout.value.LayoutParametersList?.length) {
        return DEFAULT_VALUE
    }
    const resultValue = getActiveLayoutParameterByName('ReportSwitching', 'Value')
    return Boolean(resultValue === '1')
})
export const layoutSwitchInterval = computed(() => {
    const DEFAULT_VALUE = undefined
    if (!activeLayout.value.LayoutParametersList?.length) {
        return DEFAULT_VALUE
    }
    const resultValue = getActiveLayoutParameterByName('ReportInterval', 'Value')
    return resultValue !== undefined ? Number(resultValue) : DEFAULT_VALUE
})
export const activeLayoutThresholdConfig = computed(() => {
    const params = activeLayout.value.LayoutParametersList || []
    if (!params.length) {
        return defaultExtensionThresholdConfig
    }

    const config: Record<string, string> = {}

    params.forEach(el => {
        if (extensionThresholdKeys.includes(el.LayoutParameterName)) {

            config[el.LayoutParameterName] = el.Value || LAYOUT_PARAMETERS_DEFAULTS[el.LayoutParameterName]?.Value
        }
    })
    return config
})


/**
 * Computed property that returns the parsed login statuses configuration
 */
export const activeLayoutStatusesConfig = computed<StatusAlertsConfigMap>(() => {
    const params = activeLayoutParametersList.value
    const configParam = getLayoutParameterByName(params || [], 'LoginStatusesConfiguration')

    if (configParam?.ValueText) {
        try {
            return JSON.parse(configParam.ValueText)
        } catch {
            return {}
        }
    }

    return {}
})

export const isVisualAlertDisabled = computed(() => {
    // Check preview layout first if in preview/edit mode
    if (isPreviewLayout.value) {
        const previewLayout = getPreviewLayout()
        const paramValue = getLayoutParameterByName(
            previewLayout?.LayoutParametersList || [],
            'DisableVisualAlertOnThreshold',
            'Value'
        )
        return paramValue === '1'
    }
    // Fall back to active layout
    const paramValue = getActiveLayoutParameterByName('DisableVisualAlertOnThreshold', 'Value')
    return paramValue === '1'
})

export const activeLayoutColors = computed(() => {
    return getLayoutColors(getPreviewLayout() || activeLayout.value)
})
export const activeLayoutLogo = computed(() => {
    return getLayoutLogo(activeLayout.value)
})

/* Methods */
export async function setupActiveLayout () {
    const dashboard = ActiveDashboardData.value
    let layoutToSetup: ILayoutItem | undefined
    if (!dashboard) {
        const layouts = await LayoutApi.get({ LayoutAccountID: 0 }) ?? []
        layoutToSetup = layouts[0]
    } else {
        const { DashboardLayoutID } = dashboard
        const data = {
            LayoutID: DashboardLayoutID,
        }
        try {
            const layouts = await LayoutApi.get(data) ?? []
            layoutToSetup = layouts[0]
        } catch (e) {
            const layouts = await LayoutApi.get({ LayoutAccountID: 0 }) ?? []
            layoutToSetup = layouts[0]
        }
    }
    if (!layoutToSetup) {
        layoutToSetup = defaultLayout()
    }

    setActiveLayout(layoutToSetup)
    setFavicon()
}

export function setActiveLayout (layout?: ILayoutItem | ILayoutUpsertItem) {
    activeLayoutStore.value = layout ? { ...layout } : layout
    setFavicon()
}

export function getLayoutFavIcon () {
    return getLayoutParameterByName(activeLayout.value?.LayoutParametersList || [], 'Favicon')
}

export function setFavicon (icon?: string) {
    const _defaultIcon = String(i18n.global.t('branding.Favicon'))
    let iconToSet = ''
    if (!activeLayout.value?.LayoutParametersList) {
        iconToSet = _defaultIcon
    } else {
        const logoParameter = getLayoutFavIcon()
        if (logoParameter) {
            if (icon) {
                logoParameter['ValueText'] = icon
                setActiveLayout(activeLayout.value)
            }
            iconToSet = logoParameter.ValueText
        }
    }

    const favicon = document.getElementById('favicon')

    if (favicon instanceof HTMLLinkElement) {
        favicon.href = icon || iconToSet || _defaultIcon
    }
}

export function setLayoutLogo (logo: string) {
    const _layout = activeLayout.value
    if (!_layout) {
        return
    }
    const logoParameterIndex = _layout.LayoutParametersList.findIndex(el => el.LayoutParameterName === 'DashboardLogo')
    if (logoParameterIndex !== -1) {
        _layout.LayoutParametersList[logoParameterIndex]['ValueText'] = logo
    }

    setActiveLayout(_layout)
}

export function getActiveLayoutColors () {
    return getLayoutColors(activeLayout.value)
}

export function getLayoutColors (layout?: ILayoutItem) {
    const result: Record<string, string> = { ...layoutDefaultColors }
    result['primary_rgba'] = convertHex(result.primary)
    const params = layout?.LayoutParametersList || []
    if (!params.length) {
        return result
    }
    const _parameterNames = Object.keys(layoutParametersColorKeys)
    params.forEach(param => {
        const _val = param.Value
        const _name = param.LayoutParameterName
        if (_val && _parameterNames.includes(_name)) {
            result[layoutParametersColorKeys[_name]] = _val || result[layoutParametersColorKeys[_name]]
        }
    })

    return result
}

export function getLayoutFontSize (layout?: ILayoutItem) {
    const _res = getLayoutParameterByName(layout?.LayoutParametersList || [], 'FontSize')

    return _res?.Value ? Number(_res?.Value) : DEFAULT_FONT_SIZE
}

export function getLayoutLogo (layout?: ILayoutItem) {
    const DEFAULT_VALUE = String(i18n.global.t('branding.DashboardLogo'))

    const parameters = layout?.LayoutParametersList || []
    const layoutLogo = getLayoutParameterByName(parameters, 'DashboardLogo', 'ValueText')

    return layoutLogo || DEFAULT_VALUE
}

export function getActiveLayoutMinRefreshInterval () {
    const paramValue = getActiveLayoutParameterByName('MinRefreshInterval', 'Value')
    return paramValue !== undefined ? Number(paramValue) : DEFAULT_REFRESH_INTERVAL_VALUE
}

export function getActiveLayoutDelayInterval () {
    const paramValue = getActiveLayoutParameterByName('RefreshRealTimeDataDelay', 'Value')
    return paramValue !== undefined ? Number(paramValue) : DEFAULT_REFRESH_DELAY_VALUE
}

export function getActiveLayout () {
    return activeLayoutStore.value
}

export function getActiveLayoutID () {
    return activeLayoutStore.value?.LayoutID
}

export function getSoundFromActiveLayout (layoutParameterName: string) {
    const DEFAULT_VALUE = undefined
    const params = activeLayout.value?.LayoutParametersList || []
    if (!params.length) {
        return DEFAULT_VALUE
    }
    const sound = params.find((el) => el.LayoutParameterName === layoutParameterName)

    if (!sound) {
        return DEFAULT_VALUE
    }

    const currentSound = getSoundObject(allSoundList.value, sound.Value, DEFAULT_VALUE)

    if (!currentSound) {
        return DEFAULT_VALUE
    }
    return currentSound.Rec_Path
}

export function getActiveLayoutParameterByName(parameterName: string): ILayoutParametersList | undefined
export function getActiveLayoutParameterByName<T extends keyof ILayoutParametersList>(parameterName: string, returnValue: T): ILayoutParametersList[T]

export function getActiveLayoutParameterByName (parameterName: string, returnValue?: keyof ILayoutParametersList) {
    const params = activeLayout.value?.LayoutParametersList || []
    if (returnValue === undefined) {
        return getLayoutParameterByName(params, parameterName)
    }
    return getLayoutParameterByName(params, parameterName, returnValue)
}

export function getLayoutParameterByName(params: Array<ILayoutParametersList>, parameterName: string): ILayoutParametersList | undefined
export function getLayoutParameterByName<T extends keyof ILayoutParametersList>(params: Array<ILayoutParametersList>, parameterName: string, returnValue: T): ILayoutParametersList[T]

export function getLayoutParameterByName (
    params: Array<ILayoutParametersList>,
    parameterName: string,
    returnValue?: keyof ILayoutParametersList
) {
    const paramValue = params.find(
        i => i.LayoutParameterName === parameterName
    )

    if (!paramValue) {
        return undefined
    }

    if (returnValue) {
        return paramValue[returnValue]
    }

    return paramValue
}

export function getLayoutFontSizeVariables (baseFontSize: number) {
    const fontSize = baseFontSize || DEFAULT_FONT_SIZE
    return {
        '--font-size-xs': (fontSize - 4) + 'px',
        '--font-size-sm': (fontSize - 2) + 'px',
        '--font-size-base': fontSize + 'px',
        '--font-size-lg': (fontSize + 2) + 'px',
        '--font-size-xl': (fontSize + 4) + 'px',
        '--font-size-2xl': (fontSize + 6) + 'px',
        '--font-size-3xl': (fontSize + 12) + 'px',
    }
}

export function getLayoutRootVariables (colors: Record<string, string>, fontSize = DEFAULT_FONT_SIZE) {
    const rootVariables: Record<string, string> = {}

    for (const color in colors) {
        rootVariables[`--${color}-color`] = colors[color]
    }
    const fontVariables = Object.entries(getLayoutFontSizeVariables(fontSize))
    for (const [ key, value ] of fontVariables) {
        rootVariables[key] = value
    }
    rootVariables['--primary-actions'] = rootVariables['--primary-color']
    rootVariables['--active-elements'] = rootVariables['--primary-color']
    rootVariables['--ui-lines'] = rootVariables['--frames-color'] || '#EBEBEB'
    rootVariables['--support-text'] = rootVariables['--itemsSecondaryColor-color']
    rootVariables['--colorTableHeaderText-color'] = colors.colorTableHeaderText || '#606266'

    return rootVariables
}

export function getDefaultStatusWarningSound () {
    return DEFAULT_WARNING_SOUND?.RecordID || 2
}

export function getDefaultStatusLimitSound () {
    return DEFAULT_ERROR_SOUND?.RecordID || 2
}

export function getDefaultStatusConfig (): StatusAlertConfig {
    return {
        warning: 60,
        limit: 1800,
        warningSound: getDefaultStatusWarningSound(),
        limitSound: getDefaultStatusLimitSound(),
        disableStatusAlerts: false
    }
}

export function getStatusConfiguration (statusId: number): StatusAlertConfig {
    const config = activeLayoutStatusesConfig.value[statusId]
    const defaults = getDefaultStatusConfig()

    if (config) {
        return {
            ...defaults,
            ...config
        }
    }
    return { ...defaults }
}

export function getStatusConfigValue<K extends keyof StatusAlertConfig> (
    statusId: number,
    key: K
): StatusAlertConfig[K] {
    const config = getStatusConfiguration(statusId)
    return config[key]
}

export function getStatusThresholdSound (soundId: string | number, type:string) {
    let sound
    if (`${soundId}` === `${NONE_SOUND_ID}`) {
        return null
    }
    if (type === warningThresholdValue ) {
        sound = getSoundObject(warningSoundList.value, soundId, DEFAULT_WARNING_SOUND)
    }
    if (type === limitThresholdValue) {
        sound = getSoundObject(errorSoundList.value, soundId, DEFAULT_ERROR_SOUND)
    }
    return sound?.Rec_Path || null
}

export function isStatusAlertsDisabled (statusId: number): boolean {
    // Call statuses (IN_CALL, HOLD, CALLING) should never show alerts
    if (isStatusIsCallStatus(statusId)) {
        return true
    }

    const config = activeLayoutStatusesConfig.value[statusId]
    return !!config?.disableStatusAlerts
}

/**
 * Check if a status has any threshold configuration
 */
export function hasStatusThresholdConfig (statusId: number): boolean {
    const config = activeLayoutStatusesConfig.value[statusId]
    if (!config) return false

    return !!(config.warning || config.limit)
}
